import { FC, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Dropdown, Menu } from 'antd';
import { TbPlug, TbRefresh, TbSignature } from 'react-icons/tb';
import { useLocation } from 'react-router-dom';

import DocuSignSVGIcon from '@/assets/svgs/products/docusign.svg';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    AttachmentDestination,
    CloudAttachment,
    CloudStorageConnection,
    createDocuSignSenderView,
    DocuSignConnection,
    DocuSignSignature,
    getDocuSignConnections,
    getDocuSignOAuthUrl,
    getDocuSignSignatures,
} from '@/services/database/attachments';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import DropdownOptionContent from '@/shared/components/dropdowns/DropdownOptionContent';
import DropdownPanel, { DropdownMenuLabel, DropdownPanelMenu } from '@/shared/components/dropdowns/DropdownPanel';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import { EmptyView } from '@/shared/components/layouts/views/EmptySection';
import { BaseSkeleton } from '@/shared/components/skeletons';
import { toast } from '@/shared/components/toasts/PlatformToast';

interface DocuSignActionDropdownProps {
    attachment: CloudAttachment;
    connection: CloudStorageConnection;
    destination: AttachmentDestination;
}

interface SignatureChoice {
    connection: DocuSignConnection;
    signature: DocuSignSignature;
}

const DocuSignAvatar: FC<{ size?: number }> = ({ size = 20 }) => (
    <Avatar
        icon={<img alt='' src={DocuSignSVGIcon} style={{ height: '100%', objectFit: 'contain', width: '100%' }} />}
        shape='square'
        size={size}
    />
);

const DocuSignConnectAvatar: FC = () => {
    const { token } = useAntDesignTheme();

    return (
        <span style={{ display: 'inline-flex', padding: '0 0.25rem 0.125rem 0', position: 'relative' }}>
            <DocuSignAvatar size={32} />
            <Avatar
                icon={<TbPlug size='0.75rem' />}
                shape='square'
                size={16}
                style={{
                    background: token.colorBgContainer,
                    border: `1px solid ${token.colorBorder}`,
                    bottom: 0,
                    color: token.colorTextTertiary,
                    position: 'absolute',
                    right: 0,
                }}
            />
        </span>
    );
};

const SignaturePreview: FC<{ signature: DocuSignSignature }> = ({ signature }) => (
    <span
        style={{
            alignItems: 'center',
            display: 'inline-flex',
            height: '2rem',
            justifyContent: 'center',
            overflow: 'hidden',
            width: '5rem',
        }}
    >
        {signature.previewDataUrl ? (
            <img
                alt={`${signature.name} signature`}
                src={signature.previewDataUrl}
                style={{ height: '100%', maxWidth: '100%', objectFit: 'contain' }}
            />
        ) : (
            <span
                style={{
                    fontFamily: signature.font ? `${signature.font}, cursive` : 'cursive',
                    fontSize: '1rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {signature.initials || signature.name}
            </span>
        )}
    </span>
);

const DocuSignActionDropdown: FC<DocuSignActionDropdownProps> = ({ attachment, connection, destination }) => {
    const location = useLocation();
    const [connections, setConnections] = useState<DocuSignConnection[]>([]);
    const [connecting, setConnecting] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [preparing, setPreparing] = useState(false);
    const [search, setSearch] = useState('');
    const [signatureChoices, setSignatureChoices] = useState<SignatureChoice[]>([]);

    const loadDocuSignOptions = async (force = false) => {
        if ((hasLoaded && !force) || loading) {
            return;
        }

        try {
            setLoading(true);
            const nextConnections = await getDocuSignConnections();
            const signaturesByConnection = await Promise.all(
                nextConnections.map(async (docuSignConnection) => {
                    try {
                        const signatures = await getDocuSignSignatures(docuSignConnection);

                        return signatures.map((signature) => ({ connection: docuSignConnection, signature }));
                    } catch (error) {
                        captureException(error);
                        return [];
                    }
                }),
            );

            setConnections(nextConnections);
            setSignatureChoices(signaturesByConnection.flat());
            setHasLoaded(true);
        } catch (error) {
            captureException(error);
            toast.error('Unable to load DocuSign connections');
        } finally {
            setLoading(false);
        }
    };

    const prepareSenderView = async (docuSignConnection: DocuSignConnection) => {
        try {
            setDropdownOpen(false);
            setPreparing(true);
            const senderView = await createDocuSignSenderView(
                docuSignConnection,
                connection,
                destination,
                attachment,
                `${location.pathname}${location.search}`,
            );

            toast.success('DocuSign envelope draft created', {
                description: 'Continue in DocuSign to add recipients and signature fields.',
            });
            window.location.assign(senderView.url);
        } catch (error) {
            captureException(error);
            toast.error('Unable to prepare this attachment in DocuSign');
            setPreparing(false);
        }
    };

    const connectDocuSign = async () => {
        if (connecting) {
            return;
        }

        try {
            setConnecting(true);
            const url = await getDocuSignOAuthUrl();

            window.location.assign(url);
        } catch (error) {
            captureException(error);
            toast.error('Unable to start the DocuSign connection');
            setConnecting(false);
        }
    };
    const normalizedSearch = search.trim().toLowerCase();
    const filteredSignatureChoices = useMemo(
        () =>
            signatureChoices.filter(({ connection: docuSignConnection, signature }) =>
                [docuSignConnection.label, signature.name, signature.initials, signature.font]
                    .filter(Boolean)
                    .some((value) => value?.toLowerCase().includes(normalizedSearch)),
            ),
        [normalizedSearch, signatureChoices],
    );
    const filteredConnections = useMemo(
        () =>
            connections.filter((docuSignConnection) =>
                docuSignConnection.label.toLowerCase().includes(normalizedSearch),
            ),
        [connections, normalizedSearch],
    );
    const connectionsWithSignatures = useMemo(
        () => new Set(signatureChoices.map(({ connection: docuSignConnection }) => docuSignConnection.id)),
        [signatureChoices],
    );
    const connectionFallbacks = filteredConnections.filter(
        (docuSignConnection) => !connectionsWithSignatures.has(docuSignConnection.id),
    );
    const hasVisibleOptions = filteredSignatureChoices.length > 0 || connectionFallbacks.length > 0;

    return (
        <Dropdown
            open={dropdownOpen}
            placement='bottomLeft'
            trigger={['click']}
            onOpenChange={(nextOpen, info) => {
                if (!nextOpen && info.source === 'menu') {
                    return;
                }

                setDropdownOpen(nextOpen);

                if (nextOpen) {
                    void loadDocuSignOptions();
                } else {
                    setSearch('');
                }
            }}
            popupRender={() => (
                <DropdownPanel
                    actions={
                        <DropdownPanelMenu selectable={false}>
                            <Menu.Item
                                disabled={loading}
                                key='refresh'
                                onClick={() => {
                                    setDropdownOpen(true);
                                    void loadDocuSignOptions(true);
                                }}
                            >
                                <TbRefresh style={{ marginRight: '0.375rem', verticalAlign: '-0.125rem' }} />
                                Refresh Signatures
                            </Menu.Item>
                            <Menu.Item disabled={connecting} key='connect' onClick={() => void connectDocuSign()}>
                                <DropdownOptionContent
                                    singleLine
                                    metadata={['Authorize securely with DocuSign']}
                                    title='Connect DocuSign'
                                    titlePrefix={<DocuSignConnectAvatar />}
                                />
                            </Menu.Item>
                        </DropdownPanelMenu>
                    }
                    footer={<LearnMoreFooter topic='DocuSign eSignature' variant='dropdown' />}
                    search={{
                        disabled: loading,
                        placeholder: 'Search Signatures...',
                        setValue: setSearch,
                        value: search,
                    }}
                    style={{ marginTop: '0.5rem' }}
                    width={16 * 30}
                >
                    <DropdownMenuLabel icon={<TbSignature size='0.875rem' />}>
                        {filteredSignatureChoices.length} Saved Signature
                        {filteredSignatureChoices.length === 1 ? '' : 's'}
                    </DropdownMenuLabel>
                    <DropdownPanelMenu selectable={false}>
                        {loading ? (
                            <Menu.Item disabled key='loading-signatures'>
                                <BaseSkeleton title={false} paragraph={{ rows: 3 }} />
                            </Menu.Item>
                        ) : hasVisibleOptions ? (
                            [
                                ...filteredSignatureChoices.map(({ connection: docuSignConnection, signature }) => (
                                    <Menu.Item
                                        key={`${docuSignConnection.id}:${signature.id}`}
                                        onClick={() => void prepareSenderView(docuSignConnection)}
                                    >
                                        <DropdownOptionContent
                                            singleLine
                                            metadata={[
                                                docuSignConnection.label,
                                                signature.isDefault ? 'Default Signature' : 'Saved Signature',
                                            ]}
                                            title={signature.name}
                                            titlePrefix={<SignaturePreview signature={signature} />}
                                        />
                                    </Menu.Item>
                                )),
                                ...connectionFallbacks.map((docuSignConnection) => (
                                    <Menu.Item
                                        key={docuSignConnection.id}
                                        onClick={() => void prepareSenderView(docuSignConnection)}
                                    >
                                        <DropdownOptionContent
                                            singleLine
                                            avatar={{ icon: <img alt='' src={DocuSignSVGIcon} /> }}
                                            metadata={['No saved signature preview', 'Continue in DocuSign']}
                                            title={docuSignConnection.label}
                                        />
                                    </Menu.Item>
                                )),
                            ]
                        ) : (
                            <EmptyView
                                description={
                                    connections.length
                                        ? 'No saved DocuSign signatures match this search.'
                                        : 'Connect DocuSign to use saved signatures with this attachment.'
                                }
                                height='12.5rem'
                                icon={<TbSignature />}
                                label={connections.length ? 'No Matching Signatures' : 'No DocuSign Connection'}
                            />
                        )}
                    </DropdownPanelMenu>
                </DropdownPanel>
            )}
        >
            <span>
                <DropdownButton disabled={preparing} loading={preparing} size='small'>
                    <DocuSignAvatar />
                    eSignature
                </DropdownButton>
            </span>
        </Dropdown>
    );
};

export default DocuSignActionDropdown;
